import { ChevronUp, Loader2, Pencil, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChatListItem } from "../services/chatApi";
import { chatHref } from "../utils/chatHref";
import { formatChatTimestamp } from "../utils/formatChatTimestamp";

interface ChatHistorySectionProps {
	isExpanded: boolean;
	onToggle: () => void;
	chats: ChatListItem[];
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	deleteError?: string | null;
	stale?: boolean;
	activeChatId: string | null;
	streamingChatIds?: ReadonlySet<string>;
	hasMore: boolean;
	onChatSelect: (chatId: string) => void;
	onLoadMore: () => void;
	onDeleteChat: (chatId: string) => Promise<void>;
	onRenameChat: (chatId: string, title: string) => void;
}

interface ContextMenuState {
	chatId: string;
	x: number;
	y: number;
}

const ANIMATION_DURATION_MS = 300;

const formatTimestamp = formatChatTimestamp;

const ChatHistorySection: React.FC<ChatHistorySectionProps> = ({
	isExpanded,
	onToggle,
	chats,
	loading,
	loadingMore,
	error,
	deleteError = null,
	stale = false,
	activeChatId,
	streamingChatIds,
	hasMore,
	onChatSelect,
	onLoadMore,
	onDeleteChat,
	onRenameChat,
}) => {
	const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(
		null,
	);
	const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
	const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
	const [renameValue, setRenameValue] = useState("");
	const renameInputRef = useRef<HTMLInputElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const handleContextMenu = useCallback(
		(e: React.MouseEvent, chatId: string) => {
			e.preventDefault();
			e.stopPropagation();
			setContextMenu({ chatId, x: e.clientX, y: e.clientY });
		},
		[],
	);

	const closeContextMenu = useCallback(() => {
		setContextMenu(null);
	}, []);

	const handleDelete = useCallback(() => {
		if (!contextMenu) return;
		const chatId = contextMenu.chatId;
		setContextMenu(null);
		setDeletingChatId(chatId);

		// Start the delete immediately; clear animation state only after both
		// the animation and the async delete have settled.
		const animationDone = new Promise<void>((resolve) =>
			setTimeout(resolve, ANIMATION_DURATION_MS),
		);
		const deleteDone = onDeleteChat(chatId);

		void Promise.allSettled([animationDone, deleteDone]).then(() => {
			setDeletingChatId(null);
		});
	}, [contextMenu, onDeleteChat]);

	const handleRenameStart = useCallback(() => {
		if (!contextMenu) return;
		const chat = chats.find((c) => c.id === contextMenu.chatId);
		if (!chat) return;
		setRenamingChatId(contextMenu.chatId);
		setRenameValue(chat.title || `Chat ${chat.id}`);
		setContextMenu(null);
	}, [contextMenu, chats]);

	const handleRenameConfirm = useCallback(() => {
		if (renamingChatId === null) return;
		const trimmed = renameValue.trim();
		if (trimmed) {
			onRenameChat(renamingChatId, trimmed);
		}
		setRenamingChatId(null);
		setRenameValue("");
	}, [renamingChatId, renameValue, onRenameChat]);

	const handleRenameCancel = useCallback(() => {
		setRenamingChatId(null);
		setRenameValue("");
	}, []);

	// Auto-focus rename input
	useEffect(() => {
		if (renamingChatId !== null && renameInputRef.current) {
			renameInputRef.current.focus();
			renameInputRef.current.select();
		}
	}, [renamingChatId]);

	// Close context menu on outside click or Escape
	useEffect(() => {
		if (!contextMenu) return;

		const handleClickOutside = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				closeContextMenu();
			}
		};
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeContextMenu();
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [contextMenu, closeContextMenu]);

	return (
		<div>
			<button
				onClick={onToggle}
				className="flex items-center justify-between w-full mb-2 hover:bg-gray-50 rounded p-1"
			>
				<span className="flex items-center gap-1.5 text-[11px] font-semibold text-text-label uppercase tracking-[0.09em]">
					Chat History
					{stale && chats.length > 0 ? (
						<span
							className="text-[10px] font-normal normal-case text-text-subtle"
							title="Couldn't refresh — showing last known list"
						>
							(stale)
						</span>
					) : null}
				</span>
				<ChevronUp
					aria-label="Toggle Chat History"
					className={`w-4 h-4 text-[#9a9ea3] transition-transform ${
						isExpanded ? "rotate-0" : "rotate-180"
					}`}
				/>
			</button>

			{isExpanded && (
				<div className="space-y-1">
					{deleteError && (
						<div
							role="alert"
							className="px-2 py-1.5 text-xs text-red-600 bg-red-50 rounded"
						>
							{deleteError}
						</div>
					)}
					{loading && chats.length === 0 ? (
						<div className="flex items-center justify-center py-4">
							<Loader2 className="w-4 h-4 animate-spin text-[#666F8D]" />
						</div>
					) : error && chats.length === 0 ? (
						<div className="px-2 py-2 text-xs text-red-500">{error}</div>
					) : chats.length === 0 ? (
						<div className="px-2 py-2 text-xs text-text-subtle">
							No chats yet
						</div>
					) : (
						<>
							{chats.map((chat) => {
								const timestamp =
									chat.last_message_at ?? chat.updated_at ?? chat.created_at;
								const isActive = activeChatId === chat.id;
								const isDeleting = deletingChatId === chat.id;
								const isChatStreaming = streamingChatIds?.has(chat.id) ?? false;

								return (
									<div
										key={chat.id}
										className="overflow-hidden transition-all ease-in-out"
										style={{
											maxHeight: isDeleting ? 0 : 100,
											opacity: isDeleting ? 0 : 1,
											transform: isDeleting
												? "translateX(-100%)"
												: "translateX(0)",
											transitionDuration: `${ANIMATION_DURATION_MS}ms`,
										}}
									>
										{renamingChatId === chat.id ? (
											<form
												onSubmit={(e) => {
													e.preventDefault();
													handleRenameConfirm();
												}}
												className="px-2 py-1"
											>
												<input
													ref={renameInputRef}
													type="text"
													value={renameValue}
													onChange={(e) => setRenameValue(e.target.value)}
													onBlur={handleRenameConfirm}
													onKeyDown={(e) => {
														if (e.key === "Escape") {
															handleRenameCancel();
														}
													}}
													className="w-full text-sm px-1 py-1 border border-[#004C4B] rounded outline-none focus:ring-1 focus:ring-[#004C4B]"
												/>
											</form>
										) : (
											<a
												href={chatHref(chat)}
												onClick={(e) => {
													if (
														e.metaKey ||
														e.ctrlKey ||
														e.shiftKey ||
														e.altKey ||
														e.button === 1
													) {
														return;
													}
													e.preventDefault();
													onChatSelect(chat.id);
												}}
												onContextMenu={(e) =>
													handleContextMenu(e, chat.id)
												}
												className={`block w-full text-left px-2 py-2 rounded transition-colors no-underline ${
													isActive
														? "bg-[#E6F3F3] text-[#004C4B] font-medium"
														: "hover:bg-black/[0.04] text-text-nav"
												}`}
											>
												<div className="flex items-start justify-between gap-2">
													<span
														className="text-sm font-normal truncate"
														style={{ lineHeight: "130%" }}
													>
														{chat.title || `Chat ${chat.id}`}
													</span>
													{isChatStreaming ? (
													<span
														className={`flex items-center gap-1 text-xs whitespace-nowrap ${
															isActive ? "text-[#004C4B]" : "text-[#2A9D8F]"
														}`}
														title="Chat in Progress"
													>
														<Loader2 className="w-3 h-3 animate-spin" />
													</span>
												) : (
													<span
														className={`text-xs whitespace-nowrap ${
															isActive
																? "text-[#004C4B]/70"
																: "text-text-subtle"
														}`}
													>
														{formatTimestamp(timestamp)}
													</span>
												)}
												</div>
											</a>
										)}
									</div>
								);
							})}

							{hasMore && (
								<button
									type="button"
									onClick={onLoadMore}
									disabled={loadingMore}
									className="w-full text-left px-2 py-2 text-xs text-[#004C4B] hover:bg-gray-50 rounded disabled:opacity-60 disabled:cursor-not-allowed"
								>
									{loadingMore ? "Loading..." : "Load more"}
								</button>
							)}
						</>
					)}
				</div>
			)}

			{/* Context Menu */}
			{contextMenu && (
				<div
					ref={menuRef}
					className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[140px]"
					style={{ top: contextMenu.y, left: contextMenu.x }}
				>
					<button
						type="button"
						onClick={handleRenameStart}
						className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#333] hover:bg-gray-100 transition-colors"
					>
						<Pencil className="w-4 h-4" />
						Rename chat
					</button>
					<button
						type="button"
						onClick={handleDelete}
						className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
					>
						<Trash2 className="w-4 h-4" />
						Delete chat
					</button>
				</div>
			)}
		</div>
	);
};

export default ChatHistorySection;
